<template>
  <div class="space-y-4 sm:space-y-6">
    <!-- Search and Filter Section -->
    <div v-if="showSearchSection" class="card">
      <div class="card-body space-y-4">
        <!-- Header Row with Search and Actions -->
        <div class="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <!-- Search Input -->
          <div v-if="showSearch" class="relative flex-1 max-w-md">
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search class="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              v-model="searchQuery"
              @input="handleSearch"
              :placeholder="searchPlaceholder"
              class="block w-full pl-10 pr-10 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-primary transition duration-200 shadow-sm hover:shadow-md focus:shadow-md"
            />
            <!-- Clear search button -->
            <button
              v-if="searchQuery"
              @click="clearSearch"
              class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
              title="Clear search"
            >
              <X class="h-4 w-4" />
            </button>
          </div>

          <!-- Action Buttons -->
          <div class="flex items-center gap-2">
            <button
              v-if="showFilters"
              @click="toggleAdvancedFilters"
              class="btn btn-outline btn-sm flex items-center gap-2"
              :class="{ 'btn-primary': showAdvancedFilters }"
            >
              <Filter class="h-4 w-4" />
              <span class="hidden sm:inline">Filters</span>
            </button>

            <!-- Custom action buttons slot -->
            <slot name="actions" />
          </div>
        </div>

        <!-- Advanced Filters (Collapsible) -->
        <div
          v-if="showAdvancedFilters && showFilters"
          class="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-4 animate-fadeIn"
        >
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <!-- Custom filter controls slot -->
            <slot name="filters" :filters="filters" :updateFilter="updateFilter" />

            <!-- Items Per Page -->
            <div v-if="showPagination">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Items per page
              </label>
              <select
                v-model="itemsPerPageLocal"
                @change="handleItemsPerPageChange"
                class="block w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition duration-200 shadow-sm hover:shadow-md"
              >
                <option v-for="option in itemsPerPageOptions" :key="option" :value="option">
                  {{ option }}
                </option>
              </select>
            </div>

            <!-- Sort Field -->
            <div v-if="sortableColumns.length > 0">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Sort by
              </label>
              <select
                v-model="sortField"
                @change="handleSortChange"
                class="block w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition duration-200 shadow-sm hover:shadow-md"
              >
                <option value="">Default</option>
                <option v-for="column in sortableColumns" :key="column.key" :value="column.key">
                  {{ column.label }}
                </option>
              </select>
            </div>

            <!-- Sort Direction -->
            <div v-if="sortableColumns.length > 0">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Order
              </label>
              <select
                v-model="sortOrder"
                @change="handleSortChange"
                :disabled="!sortField"
                class="block w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition duration-200 shadow-sm hover:shadow-md disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:shadow-none"
              >
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
            </div>
          </div>

          <!-- Clear Filters Button -->
          <div class="flex justify-end pt-2">
            <button
              @click="clearAllFilters"
              class="btn btn-outline btn-sm flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200"
            >
              <RotateCcw class="h-4 w-4" />
              Clear All Filters
            </button>
          </div>
        </div>

        <!-- Results Summary -->
        <div
          v-if="showSummary"
          class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 pt-2 border-t border-gray-100 dark:border-gray-800"
        >
          <div class="flex items-center gap-3">
            <div class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <component v-if="summaryIcon" :is="summaryIcon" class="h-4 w-4" />
              <span class="font-medium">{{ displayedData.length }}</span> of
              <span class="font-medium">{{ totalItems }}</span> {{ itemLabel }}
            </div>

            <div v-if="hasActiveFilters" class="flex items-center">
              <span
                class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 border border-blue-200 dark:border-blue-800"
              >
                <Filter class="h-3 w-3 mr-1" />
                Filtered
              </span>
            </div>
          </div>

          <!-- Custom summary content slot -->
          <slot name="summary" :totalItems="totalItems" :displayedItems="displayedData.length" />
        </div>
      </div>
    </div>

    <!-- Data Table -->
    <div class="card">
      <!-- No Results State -->
      <div v-if="displayedData.length === 0 && !loading" class="card-body text-center py-12">
        <slot name="empty-state">
          <SearchNotFoundIcon />
          <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No {{ itemLabel }} found
          </h3>
          <p class="text-gray-500 dark:text-gray-400 mb-4">
            {{
              hasActiveFilters
                ? "Try adjusting your search or filters to find what you're looking for."
                : `Get started by adding your first ${itemLabel.slice(0, -1)}.`
            }}
          </p>
          <div class="flex flex-col sm:flex-row gap-2 justify-center">
            <button v-if="hasActiveFilters" @click="clearAllFilters" class="btn btn-outline btn-sm">
              Clear Filters
            </button>
            <slot name="empty-actions" />
          </div>
        </slot>
      </div>

      <!-- Loading State -->
      <div v-else-if="loading" class="card-body flex flex-col justify-center items-center py-12">
        <slot name="loading">
          <vue-loaders
            name="line-scale"
            :color="isDarkMode ? '#818cf8' : '#4f46e5'"
            scale="1.2"
            class="mb-4"
          />
          <span class="text-gray-900 dark:text-white"
            >Loading {{ itemLabel }}...</span
          >
        </slot>
      </div>

      <!-- Table with Results -->
      <div v-else-if="displayedData.length > 0">
        <!-- Desktop Table (xl and up) -->
        <div class="hidden xl:block">
          <table class="w-full table-fixed divide-y divide-gray-200 dark:divide-gray-700">
            <thead class="bg-gray-100 dark:bg-gray-800">
              <tr>
                <th
                  v-for="column in columns"
                  :key="column.key"
                  :class="[
                    'px-6 py-3 text-left text-sm font-bold text-gray-700 dark:text-gray-200 tracking-wider',
                    column.sortable
                      ? 'cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700'
                      : '',
                    column.align === 'center'
                      ? 'text-center'
                      : column.align === 'right'
                      ? 'text-right'
                      : 'text-left'
                  ]"
                  :style="column.width ? { width: column.width } : undefined"
                  @click="column.sortable ? sortBy(column.key) : null"
                >
                  {{ column.label }}
                  <span v-if="column.sortable && currentSort.field === column.key">
                    {{ currentSort.direction === 'asc' ? '▲' : '▼' }}
                  </span>
                </th>
                <th
                  v-if="$slots.actions || $slots['row-actions']"
                  class="px-6 py-3 text-center text-sm font-bold text-gray-700 dark:text-gray-200 tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>

            <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
              <tr
                v-for="(item, index) in displayedData"
                :key="getItemKey(item, index)"
                class="hover:bg-gray-50 dark:hover:bg-gray-800 transition"
              >
                <td
                  v-for="column in columns"
                  :key="column.key"
                  :class="[
                    'px-6 py-4',
                    column.align === 'center'
                      ? 'text-center'
                      : column.align === 'right'
                      ? 'text-right'
                      : 'text-left'
                  ]"
                  :style="column.width ? { width: column.width } : undefined"
                >
                  <slot
                    :name="`cell-${column.key}`"
                    :item="item"
                    :value="getNestedValue(item, column.key)"
                  >
                    {{ formatCellValue(item, column) }}
                  </slot>
                </td>
                <td
                  v-if="$slots.actions || $slots['row-actions']"
                  class="px-6 py-4 whitespace-nowrap text-center"
                >
                  <slot name="row-actions" :item="item" :index="index">
                    <slot name="actions" :item="item" :index="index" />
                  </slot>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Mobile/Tablet Card Layout (below xl) -->
        <div class="xl:hidden space-y-4">
          <div
            v-for="(item, index) in displayedData"
            :key="getItemKey(item, index)"
            class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
          >
            <slot name="mobile-card" :item="item" :index="index">
              <!-- Default mobile card layout -->
              <div class="space-y-3">
                <div
                  v-for="column in mobileColumnsComputed"
                  :key="column.key"
                  class="flex items-center justify-between"
                >
                  <span class="text-sm font-medium text-gray-600 dark:text-gray-300">
                    {{ column.label }}:
                  </span>
                  <span
                    class="text-sm text-gray-900 dark:text-white min-w-0 max-w-[60%] flex justify-end"
                  >
                    <slot
                      :name="`mobile-${column.key}`"
                      :item="item"
                      :value="getNestedValue(item, column.key)"
                    >
                      {{ formatCellValue(item, column) }}
                    </slot>
                  </span>
                </div>

                <!-- Actions for mobile -->
                <div
                  v-if="$slots.actions || $slots['row-actions'] || $slots['mobile-actions']"
                  class="flex justify-end space-x-3 pt-3 border-t border-gray-200 dark:border-gray-600"
                >
                  <slot name="mobile-actions" :item="item" :index="index">
                    <slot name="row-actions" :item="item" :index="index">
                      <slot name="actions" :item="item" :index="index" />
                    </slot>
                  </slot>
                </div>
              </div>
            </slot>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination Controls -->
    <div
      v-if="showPagination && totalPages > 1"
      class="flex flex-col sm:flex-row justify-between sm:justify-end items-center gap-4 mt-4 px-4"
    >
      <!-- Page Info -->
      <div class="text-sm text-gray-500 dark:text-gray-400 order-2 sm:order-1">
        Page {{ currentPage }} of {{ totalPages }} ({{ totalItems }} total)
      </div>

      <!-- Pagination Controls -->
      <div class="flex space-x-1 sm:space-x-2 order-1 sm:order-2">
        <!-- Previous Button -->
        <button
          class="btn btn-sm px-2 sm:px-3"
          :disabled="currentPage === 1"
          @click="goToPage(currentPage - 1)"
          aria-label="Previous page"
        >
          <span class="hidden sm:inline">Previous</span>
          <ChevronLeft class="h-4 w-4 sm:hidden" />
        </button>

        <!-- Page Numbers -->
        <div class="hidden sm:flex space-x-1 items-center">
          <template v-if="totalPages <= 7">
            <button
              v-for="page in totalPages"
              :key="page"
              @click="goToPage(page)"
              class="btn btn-sm px-3 min-w-[2.5rem] h-9 flex items-center justify-center transition-colors duration-200"
              :class="{
                'btn-primary shadow-md': page === currentPage,
                'btn-outline hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500':
                  page !== currentPage
              }"
            >
              {{ page }}
            </button>
          </template>

          <template v-else>
            <!-- First page -->
            <button
              @click="goToPage(1)"
              class="btn btn-sm px-3 min-w-[2.5rem] h-9 flex items-center justify-center transition-colors duration-200"
              :class="{
                'btn-primary shadow-md': 1 === currentPage,
                'btn-outline hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500':
                  1 !== currentPage
              }"
            >
              1
            </button>

            <!-- Left ellipsis -->
            <span
              v-if="currentPage > 4"
              class="px-2 py-1 text-gray-500 dark:text-gray-400 flex items-center justify-center min-w-[2.5rem] h-9 text-sm font-medium"
            >
              ⋯
            </span>

            <!-- Pages around current -->
            <template v-for="page in getSmartVisiblePages()" :key="page">
              <button
                @click="goToPage(page)"
                class="btn btn-sm px-3 min-w-[2.5rem] h-9 flex items-center justify-center transition-colors duration-200"
                :class="{
                  'btn-primary shadow-md': page === currentPage,
                  'btn-outline hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500':
                    page !== currentPage
                }"
              >
                {{ page }}
              </button>
            </template>

            <!-- Right ellipsis -->
            <span
              v-if="currentPage < totalPages - 3"
              class="px-2 py-1 text-gray-500 dark:text-gray-400 flex items-center justify-center min-w-[2.5rem] h-9 text-sm font-medium"
            >
              ⋯
            </span>

            <!-- Last page -->
            <button
              v-if="totalPages > 1"
              @click="goToPage(totalPages)"
              class="btn btn-sm px-3 min-w-[2.5rem] h-9 flex items-center justify-center transition-colors duration-200"
              :class="{
                'btn-primary shadow-md': totalPages === currentPage,
                'btn-outline hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500':
                  totalPages !== currentPage
              }"
            >
              {{ totalPages }}
            </button>
          </template>
        </div>

        <!-- Mobile pagination -->
        <div class="flex sm:hidden space-x-1 items-center">
          <template v-if="totalPages <= 5">
            <button
              v-for="page in totalPages"
              :key="page"
              @click="goToPage(page)"
              class="btn btn-sm px-2 min-w-[2rem] h-8 flex items-center justify-center text-sm transition-colors duration-200"
              :class="{
                'btn-primary shadow-md': page === currentPage,
                'btn-outline hover:bg-gray-50 dark:hover:bg-gray-700': page !== currentPage
              }"
            >
              {{ page }}
            </button>
          </template>
          <template v-else>
            <button
              v-if="currentPage > 2"
              @click="goToPage(1)"
              class="btn btn-sm px-2 min-w-[2rem] h-8 flex items-center justify-center text-sm transition-colors duration-200 btn-outline hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              1
            </button>

            <span v-if="currentPage > 3" class="px-1 text-gray-500 dark:text-gray-400 text-sm"
              >⋯</span
            >

            <button
              v-if="currentPage > 1"
              @click="goToPage(currentPage - 1)"
              class="btn btn-sm px-2 min-w-[2rem] h-8 flex items-center justify-center text-sm transition-colors duration-200 btn-outline hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {{ currentPage - 1 }}
            </button>

            <button
              class="btn btn-sm px-2 min-w-[2rem] h-8 flex items-center justify-center text-sm btn-primary shadow-md"
            >
              {{ currentPage }}
            </button>

            <button
              v-if="currentPage < totalPages"
              @click="goToPage(currentPage + 1)"
              class="btn btn-sm px-2 min-w-[2rem] h-8 flex items-center justify-center text-sm transition-colors duration-200 btn-outline hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {{ currentPage + 1 }}
            </button>

            <span
              v-if="currentPage < totalPages - 2"
              class="px-1 text-gray-500 dark:text-gray-400 text-sm"
              >⋯</span
            >

            <button
              v-if="currentPage < totalPages - 1"
              @click="goToPage(totalPages)"
              class="btn btn-sm px-2 min-w-[2rem] h-8 flex items-center justify-center text-sm transition-colors duration-200 btn-outline hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {{ totalPages }}
            </button>
          </template>
        </div>

        <!-- Next Button -->
        <button
          class="btn btn-sm px-2 sm:px-3"
          :disabled="currentPage === totalPages"
          @click="goToPage(currentPage + 1)"
          aria-label="Next page"
        >
          <span class="hidden sm:inline">Next</span>
          <ChevronRight class="h-4 w-4 sm:hidden" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue'
import { useThemeStore } from '@/stores/theme'
import SearchNotFoundIcon from '@/components/icons/SearchNotFoundIcon.vue'
import { Search, X, Filter, RotateCcw, ChevronLeft, ChevronRight } from 'lucide-vue-next'

// Types
interface Column {
  key: string
  label: string
  sortable?: boolean
  formatter?: (value: any, item: any) => string
  align?: 'left' | 'center' | 'right'
  width?: string
}

interface SortableColumn {
  key: string
  label: string
}

interface Sort {
  field: string
  direction: 'asc' | 'desc'
}

// Props
interface Props {
  data: any[]
  columns: Column[]
  itemKey?: string
  loading?: boolean
  showSearch?: boolean
  showFilters?: boolean
  showPagination?: boolean
  showSummary?: boolean
  showSearchSection?: boolean
  searchPlaceholder?: string
  itemLabel?: string
  itemsPerPage?: number
  itemsPerPageOptions?: number[]
  summaryIcon?: any
  filters?: Record<string, any>
  sortableColumns?: SortableColumn[]
  mobileColumns?: Column[]
  tableId?: string // Unique identifier for localStorage persistence
}

const props = withDefaults(defineProps<Props>(), {
  itemKey: 'id',
  loading: false,
  showSearch: true,
  showFilters: true,
  showPagination: true,
  showSummary: true,
  showSearchSection: true,
  searchPlaceholder: 'Search...',
  itemLabel: 'items',
  itemsPerPage: 10,
  itemsPerPageOptions: () => [5, 10, 25, 50],
  filters: () => ({}),
  sortableColumns: () => [],
  mobileColumns: () => [],
  tableId: 'default-table'
})

// Emits
const emit = defineEmits<{
  search: [query: string]
  sort: [sort: Sort]
  filter: [filters: Record<string, any>]
  pageChange: [page: number]
  itemsPerPageChange: [itemsPerPage: number]
}>()

// Theme store
const themeStore = useThemeStore()
const isDarkMode = computed(() => themeStore.isDarkMode)

// Local state
const searchQuery = ref('')
const showAdvancedFilters = ref(false)
const sortField = ref('')
const sortOrder = ref<'asc' | 'desc'>('asc')
const itemsPerPageLocal = ref(props.itemsPerPage)
const currentPage = ref(1)
const filters = ref({ ...props.filters })

// Computed properties
const currentSort = ref<Sort>({ field: '', direction: 'asc' })

const hasActiveFilters = computed(() => {
  return !!(
    searchQuery.value ||
    sortField.value ||
    Object.values(filters.value).some(v => v !== '' && v !== null && v !== undefined)
  )
})

const filteredData = computed(() => {
  let result = [...props.data]

  // Apply search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    const valueToSearchString = (val: any): string => {
      if (val === null || val === undefined) return ''
      if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean')
        return String(val)
      if (Array.isArray(val)) return val.map(v => valueToSearchString(v)).join(' ')
      if (typeof val === 'object') {
        const parts: string[] = []
        // Shallow collect common string fields (name, title, label) and primitive values
        Object.entries(val).forEach(([, v]) => {
          if (v === null || v === undefined) return
          if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
            parts.push(String(v))
            return
          }
          // If nested object has a common identifier, prefer that
          if (typeof v === 'object' && v !== null) {
            if ((v as any).name) parts.push(String((v as any).name))
            else if ((v as any).title) parts.push(String((v as any).title))
            else if ((v as any).label) parts.push(String((v as any).label))
          }
        })
        return parts.join(' ')
      }
      return String(val)
    }

    result = result.filter(item => {
      return props.columns.some(column => {
        const value = getNestedValue(item, column.key)
        return valueToSearchString(value).toLowerCase().includes(query)
      })
    })
  }

  // Apply custom filters
  Object.entries(filters.value).forEach(([key, value]) => {
    if (value !== '' && value !== null && value !== undefined) {
      result = result.filter(item => {
        const itemValue = getNestedValue(item, key)
        return itemValue === value
      })
    }
  })

  return result
})

const sortedData = computed(() => {
  if (!currentSort.value.field) return filteredData.value

  return [...filteredData.value].sort((a, b) => {
    const aValue = getNestedValue(a, currentSort.value.field)
    const bValue = getNestedValue(b, currentSort.value.field)

    let comparison = 0
    if (aValue < bValue) comparison = -1
    if (aValue > bValue) comparison = 1

    return currentSort.value.direction === 'desc' ? -comparison : comparison
  })
})

const totalItems = computed(() => sortedData.value.length)
const totalPages = computed(() => Math.ceil(totalItems.value / itemsPerPageLocal.value))

const displayedData = computed(() => {
  if (!props.showPagination) return sortedData.value

  const start = (currentPage.value - 1) * itemsPerPageLocal.value
  const end = start + itemsPerPageLocal.value
  return sortedData.value.slice(start, end)
})

const mobileColumnsComputed = computed(() => {
  return props.mobileColumns.length > 0 ? props.mobileColumns : props.columns.slice(0, 3)
})

// Methods
const getItemKey = (item: any, index: number): string | number => {
  return getNestedValue(item, props.itemKey) || index
}

const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((o, p) => o?.[p], obj)
}

const formatCellValue = (item: any, column: Column): string => {
  const value = getNestedValue(item, column.key)

  if (column.formatter) {
    return column.formatter(value, item)
  }

  return String(value || '')
}

const handleSearch = () => {
  currentPage.value = 1
  emit('search', searchQuery.value)
}

const clearSearch = () => {
  searchQuery.value = ''
  currentPage.value = 1
  emit('search', '')
}

const toggleAdvancedFilters = () => {
  showAdvancedFilters.value = !showAdvancedFilters.value
}

const updateFilter = (key: string, value: any) => {
  filters.value[key] = value
  currentPage.value = 1
  emit('filter', { ...filters.value })
}

const sortBy = (field: string) => {
  if (currentSort.value.field === field) {
    currentSort.value.direction = currentSort.value.direction === 'asc' ? 'desc' : 'asc'
  } else {
    currentSort.value.field = field
    currentSort.value.direction = 'asc'
  }

  // Update form controls to match
  sortField.value = field
  sortOrder.value = currentSort.value.direction

  emit('sort', { ...currentSort.value })
}

const handleSortChange = () => {
  if (sortField.value) {
    currentSort.value.field = sortField.value
    currentSort.value.direction = sortOrder.value
  } else {
    // Reset sorting when "Default" is selected
    currentSort.value = { field: '', direction: 'asc' }
  }
  emit('sort', { ...currentSort.value })
}

const handleItemsPerPageChange = () => {
  currentPage.value = 1
  emit('itemsPerPageChange', itemsPerPageLocal.value)
}

const goToPage = (page: number) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
    emit('pageChange', page)
  }
}

const clearAllFilters = () => {
  searchQuery.value = ''
  sortField.value = ''
  sortOrder.value = 'asc'
  currentSort.value = { field: '', direction: 'asc' }
  itemsPerPageLocal.value = props.itemsPerPage
  // Reset all filters to empty strings
  Object.keys(filters.value).forEach(key => {
    filters.value[key] = ''
  })
  currentPage.value = 1

  emit('search', '')
  emit('sort', { field: '', direction: 'asc' })
  emit('filter', { ...filters.value })
  emit('itemsPerPageChange', props.itemsPerPage)
}

const getSmartVisiblePages = () => {
  const pages: number[] = []

  if (currentPage.value <= 4) {
    for (let i = 2; i <= Math.min(5, totalPages.value - 1); i++) {
      pages.push(i)
    }
  } else if (currentPage.value >= totalPages.value - 3) {
    for (let i = Math.max(totalPages.value - 4, 2); i <= totalPages.value - 1; i++) {
      pages.push(i)
    }
  } else {
    for (let i = currentPage.value - 1; i <= currentPage.value + 1; i++) {
      if (i > 1 && i < totalPages.value) {
        pages.push(i)
      }
    }
  }

  return pages
}

// Watch for external data changes to reset pagination
// Only reset to page 1 when the data length changes (items added/removed),
// not when individual items are modified (e.g., status updates)
watch(
  () => props.data.length,
  () => {
    currentPage.value = 1
  }
)

// Watch for external filter changes
watch(
  () => props.filters,
  newFilters => {
    filters.value = { ...newFilters }
  },
  { deep: true }
)

// Component initialization
onMounted(() => {
  if (props.showPagination) {
    itemsPerPageLocal.value = props.itemsPerPage
  }
})
</script>

<style scoped>
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fadeIn {
  animation: fadeIn 0.3s ease-in-out;
}

/* Custom focus styles for better accessibility */
.focus\:ring-primary:focus {
  --tw-ring-opacity: 0.5;
  --tw-ring-color: rgb(79 70 229 / var(--tw-ring-opacity));
}

/* Hover effects for interactive elements */
input:hover,
select:hover {
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
}

/* Dark mode ring colors */
.dark .focus\:ring-primary:focus {
  --tw-ring-color: rgb(129 140 248 / var(--tw-ring-opacity));
}
</style>
